<script setup>
import { ref, computed } from 'vue'
import { format } from '@formkit/tempo'
import ContentFrame from '@components/global/ContentFrame.vue'

const perPage = 5

const props = defineProps({
	title: {
		type: String,
		required: true,
	},
	results: {
		type: Array,
		default: () => [],
	},
})

const visible = ref(perPage)

const visibleResults = computed(() => props.results.slice(0, visible.value))

const ordinal = (place) => {
	const suffixes = { one: 'st', two: 'nd', few: 'rd', other: 'th' }
	return `${place}${suffixes[new Intl.PluralRules('en-GB', { type: 'ordinal' }).select(place)]}`
}

/* A tournament without a start date is still worth listing, so the cell empties instead. */
const date = (value) => (value ? format({ date: value, format: 'DD/MM/YYYY', tz: 'UTC' }) : '')

const loadMore = () => {
	visible.value += perPage
}
</script>

<template>
	<ContentFrame class="overflow-hidden" :header="{ type: 'compact', title }">
		<div class="overflow-x-auto">
			<table>
				<thead>
					<tr>
						<th class="w-12">Place</th>
						<th class="w-24">Tier</th>
						<th class="min-w-56">Tournament</th>
						<th>Date</th>
						<th>Points</th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="result in visibleResults" :key="result.slug">
						<td :class="['text-center text-xs tracking-wide uppercase', { first: result.place === 1 }, { second: result.place === 2 }, { third: result.place === 3 }]">{{ result.place ? ordinal(result.place) : '' }}</td>
						<td class="font-noto-sans font-normal opacity-70">{{ result.tier }}</td>
						<td>
							<!-- Only a tournament with an event page is worth linking, so the rest read as plain text. -->
							<router-link class="text-snk-blue-500 hover:text-snk-blue-700 font-semibold uppercase transition-colors duration-200" :to="{ name: 'tournament', params: { slug: result.slug } }" v-if="result.page">{{ result.tournament }}</router-link>
							<div class="font-normal opacity-60" v-else>{{ result.tournament }}</div>
							<div class="font-noto-sans mt-0.5 text-xs font-normal opacity-70" v-if="result.qualified_for?.length">Qualified for {{ result.qualified_for.join(', ') }}</div>
						</td>
						<td class="font-noto-sans font-normal text-nowrap opacity-70">{{ date(result.date) }}</td>
						<td class="font-semibold">{{ (result.points ?? 0).toLocaleString('en-GB') }}</td>
					</tr>
					<tr v-if="results.length === 0">
						<td colspan="5" class="text-center">There are no results to show yet.</td>
					</tr>
				</tbody>
			</table>
		</div>
		<button class="bg-snk-light-500 font-poppins hover:bg-snk-light-800 flex w-full items-center justify-center gap-1.5 px-5 py-3.5 text-xs font-semibold uppercase transition-colors duration-300" @click="loadMore" v-if="visible < results.length">
			Load more
			<i class="fas fa-chevron-down" />
		</button>
	</ContentFrame>
</template>

<style scoped>
@reference '../../../css/theme.css';

/* The global table styles draw their own border and radius, which the frame already provides. */
table:before {
	@apply hidden;
}
table th {
	@apply bg-snk-light-500 text-snk-dark-500 py-4 font-semibold tracking-wide first:pl-5 last:pr-5;
}
table td {
	@apply border-t-0 py-3 first:pl-5 last:pr-5;
}
table th:nth-child(5),
table td:nth-child(5) {
	@apply text-right;
}
/* Keep the placement accent bar inside the row's horizontal padding. */
table td:before {
	@apply left-5;
}
tbody tr:nth-child(odd) {
	@apply bg-white;
}
tbody tr:nth-child(even) {
	@apply bg-snk-light-300;
}
</style>
